import React, { useState, useEffect } from 'react';
import './Onboarding.css';

const Onboarding = ({ onComplete, onSkip, currentUser, primaryColor = '#8CC63F' }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // Appliquer la couleur primaire dynamiquement au montage du composant
  useEffect(() => {
    if (primaryColor) {
      document.documentElement.style.setProperty('--onboarding-primary-color', primaryColor);
      // Calculer une version plus foncée pour les dégradés
      const darkerColor = adjustColorBrightness(primaryColor, -20);
      document.documentElement.style.setProperty('--onboarding-primary-dark', darkerColor);
    }
  }, [primaryColor]);

  // Fonction pour ajuster la luminosité d'une couleur
  const adjustColorBrightness = (color, amount) => {
    const usePound = color[0] === '#';
    const col = usePound ? color.slice(1) : color;
    const num = parseInt(col, 16);
    let r = (num >> 16) + amount;
    let g = ((num >> 8) & 0x00FF) + amount;
    let b = (num & 0x0000FF) + amount;
    r = r > 255 ? 255 : r < 0 ? 0 : r;
    g = g > 255 ? 255 : g < 0 ? 0 : g;
    b = b > 255 ? 255 : b < 0 ? 0 : b;
    return (usePound ? '#' : '') + (r << 16 | g << 8 | b).toString(16).padStart(6, '0');
  };

  // Définition des étapes de l'onboarding
  const steps = [
    {
      id: 'welcome',
      title: '🌱 Bienvenue dans Maraich\'Pro !',
      description: 'Votre solution complète pour gérer votre activité de maraîchage professionnel',
      content: (
        <div className="onboarding-welcome">
          <div className="welcome-icon">🎉</div>
          <h2>Ravi de vous accueillir !</h2>
          <p>
            Maraich'Pro va vous simplifier la vie au quotidien : gestion des clients, 
            catalogue produits, commandes, facturation... tout au même endroit !
          </p>
          <div className="welcome-features">
            <div className="feature-item">
              <span className="feature-icon">✅</span>
              <span>Gestion complète des commandes</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">📊</span>
              <span>Statistiques en temps réel</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">💾</span>
              <span>Export comptable simplifié</span>
            </div>
          </div>
          <p className="welcome-note">
            <strong>Prenez 2 minutes</strong> pour découvrir les principales fonctionnalités. 
            Vous pourrez toujours relancer cette visite plus tard !
          </p>
        </div>
      ),
      highlight: null
    },
    {
      id: 'dashboard',
      title: '📊 Tableau de bord',
      description: 'Votre vue d\'ensemble en un coup d\'œil',
      content: (
        <div className="onboarding-content">
          <p>
            Le <strong>tableau de bord</strong> affiche vos indicateurs clés :
          </p>
          <ul>
            <li>📈 Chiffre d'affaires du mois en cours</li>
            <li>🛒 Nombre de commandes actives</li>
            <li>👥 Clients enregistrés</li>
            <li>📦 Produits en stock</li>
          </ul>
          <p className="tip">
            💡 <strong>Astuce :</strong> Consultez-le chaque matin pour suivre votre activité !
          </p>
        </div>
      ),
      highlight: 'button:has-text("Tableau de bord")'
    },
    {
      id: 'clients',
      title: '👥 Gestion des Clients',
      description: 'Centralisez toutes les informations de vos clients',
      content: (
        <div className="onboarding-content">
          <p>
            Dans l'onglet <strong>Clients</strong>, vous pouvez :
          </p>
          <ul>
            <li>➕ Ajouter de nouveaux clients (restaurateurs, GMS, boutiques...)</li>
            <li>📝 Enregistrer leurs coordonnées complètes</li>
            <li>🏷️ Classer par type de client</li>
            <li>✏️ Modifier ou supprimer des fiches</li>
          </ul>
          <p className="tip">
            💡 <strong>Astuce :</strong> Ajoutez vos clients en amont pour gagner du temps ! Vous ne pourrez pas créer de client directement lors de la saisie d'une commande.
          </p>
        </div>
      ),
      highlight: 'button:has-text("Clients")'
    },
    {
      id: 'produits',
      title: '🥕 Gestion des Produits',
      description: 'Créez et gérez votre catalogue',
      content: (
        <div className="onboarding-content">
          <p>
            L'onglet <strong>Produits</strong> vous permet de :
          </p>
          <ul>
            <li>🌽 Créer votre catalogue de légumes et produits</li>
            <li>💰 Définir des prix différents par type de client</li>
            <li>📦 Suivre vos stocks en temps réel</li>
            <li>🌿 Marquer les produits bio</li>
            <li>🍂 Gérer les produits en fin de saison</li>
          </ul>
          <p className="tip">
            💡 <strong>Astuce :</strong> En masquant vos produits qui sont hors saison, vous gagnez en fluidité pour gérer vos produits et vos commandes !
          </p>
        </div>
      ),
      highlight: 'button:has-text("Produits")'
    },
    {
      id: 'commandes',
      title: '🛒 Gestion des Commandes',
      description: 'Le cœur de votre activité',
      content: (
        <div className="onboarding-content">
          <p>
            C'est ici que tout se passe ! Dans <strong>Commandes</strong> :
          </p>
          <ul>
            <li>📝 Créez rapidement de nouvelles commandes</li>
            <li>💵 Personnalisez les prix si besoin</li>
            <li>📋 Suivez l'état : brouillon → en cours → livrée → facturée</li>
            <li>🧾 Générez bons de livraison et factures en PDF</li>
            <li>📜 Créez des avoirs en cas d'annulation</li>
          </ul>
          <p className="tip">
            💡 <strong>Astuce :</strong> Utilisez les brouillons pour préparer vos commandes à l'avance !
          </p>
        </div>
      ),
      highlight: 'button:has-text("Commandes")'
    },
    currentUser?.role === 'admin' ? {
      id: 'statistiques',
      title: '📈 Statistiques',
      description: 'Analysez vos performances',
      content: (
        <div className="onboarding-content">
          <p>
            En tant qu'<strong>administrateur</strong>, vous avez accès aux statistiques :
          </p>
          <ul>
            <li>📊 Évolution du chiffre d'affaires</li>
            <li>🥇 Classement des meilleurs clients</li>
            <li>🌟 Produits les plus vendus</li>
            <li>📅 Analyse par période</li>
          </ul>
          <p className="tip">
            💡 <strong>Astuce :</strong> Utilisez ces données pour optimiser votre production !
          </p>
        </div>
      ),
      highlight: 'button:has-text("Statistiques")'
    } : null,
    currentUser?.role === 'comptable' ? {
      id: 'export',
      title: '📤 Export Comptable',
      description: 'Exportez vos données facilement',
      content: (
        <div className="onboarding-content">
          <p>
            L'onglet <strong>Export</strong> vous permet de :
          </p>
          <ul>
            <li>📥 Exporter toutes vos factures</li>
            <li>📅 Choisir une période spécifique</li>
            <li>📊 Obtenir un fichier Excel avec tous les détails</li>
            <li>💼 Parfait pour votre comptabilité !</li>
          </ul>
          <p className="tip">
            💡 <strong>Astuce :</strong> Exportez vos données chaque mois pour faciliter votre suivi comptable !
          </p>
        </div>
      ),
      highlight: 'button:has-text("Export")'
    } : {
      id: 'parametres',
      title: '⚙️ Paramètres',
      description: 'Personnalisez votre application',
      content: (
        <div className="onboarding-content">
          <p>
            Dans les <strong>Paramètres</strong>, vous pouvez :
          </p>
          <ul>
            <li>🏢 Configurer les informations de votre entreprise</li>
            <li>🎨 Personnaliser la couleur de l'interface</li>
            <li>📝 Définir vos types de clients</li>
            <li>📦 Configurer la gestion de stock</li>
            <li>📤 Exporter vos données comptables</li>
          </ul>
          <p className="tip">
            💡 <strong>Astuce :</strong> Prenez le temps de bien renseigner vos informations, elles apparaîtront sur vos factures !
          </p>
        </div>
      ),
      highlight: 'button:has-text("Paramètres")'
    },
    {
      id: 'support',
      title: '🆘 Support à votre disposition',
      description: 'Nous sommes là pour vous aider',
      content: (
        <div className="onboarding-content">
          <div className="support-image-container">
            <img 
              src="/support-contact-cropped.jpg" 
              alt="Support Maraich'Pro" 
              className="support-image"
            />
          </div>
          <p>
            Vous n'êtes pas seul ! Notre équipe est là pour vous accompagner.
          </p>
          <ul>
            <li>❓ Une question sur l'utilisation ?</li>
            <li>💡 Besoin d'un conseil ?</li>
            <li>🐛 Un problème technique ?</li>
            <li>✨ Une suggestion d'amélioration ?</li>
          </ul>
          <div className="support-contact">
            <p><strong>Contactez-nous facilement :</strong></p>
            <a 
              href="https://maraichpro.com/#contact" 
              target="_blank" 
              rel="noopener noreferrer"
              className="support-link"
            >
              📧 Formulaire de contact sur maraichpro.com
            </a>
          </div>
          <p className="tip">
            💡 <strong>Astuce :</strong> N'hésitez pas à nous contacter, même pour une petite question. Nous sommes là pour ça !
          </p>
        </div>
      ),
      highlight: null
    },
    {
      id: 'complete',
      title: '🎉 Félicitations !',
      description: 'Vous êtes prêt à démarrer',
      content: (
        <div className="onboarding-complete">
          <div className="complete-icon">🚀</div>
          <h2>Vous voilà paré pour l'aventure !</h2>
          <p>
            Vous connaissez maintenant les principales fonctionnalités de Maraich'Pro.
          </p>
          <div className="complete-actions">
            <p><strong>Pour bien démarrer, nous vous suggérons de :</strong></p>
            <ol>
              <li>Ajouter vos premiers clients</li>
              <li>Créer votre catalogue de produits</li>
              <li>Enregistrer votre première commande</li>
            </ol>
          </div>
          <p className="complete-note">
            💡 Vous pouvez relancer cette visite à tout moment en cliquant sur l'icône <strong>❓</strong> en haut à droite de l'écran.
          </p>
        </div>
      ),
      highlight: null
    }
  ].filter(Boolean); // Enlever les étapes null

  const totalSteps = steps.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Dernière étape : ne pas afficher les confettis automatiquement
      // Les boutons de la dernière étape gèrent la fermeture
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    setIsExiting(true);
    setTimeout(() => {
      onComplete();
    }, 300);
  };

  const handleCompleteWithConfetti = () => {
    setShowConfetti(true);
    setTimeout(() => {
      handleComplete();
    }, 2000);
  };

  const handleGoToSettings = () => {
    setShowConfetti(true);
    setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => {
        onComplete();
        // Déclencher le changement d'onglet vers Réglages
        // Cela sera géré par le parent (App.js)
        if (window.handleTabChange) {
          window.handleTabChange('reglages');
        }
      }, 300);
    }, 2000);
  };

  const handleSkipOnboarding = () => {
    setIsExiting(true);
    setTimeout(() => {
      onSkip();
    }, 300);
  };

  // Effet confettis
  useEffect(() => {
    if (showConfetti) {
      createConfetti();
    }
  }, [showConfetti]);

  const createConfetti = () => {
    // Utiliser la couleur primaire personnalisée pour les confettis
    const baseColor = primaryColor || '#8CC63F';
    const colors = [
      baseColor,
      adjustColorBrightness(baseColor, 30),  // Version plus claire
      adjustColorBrightness(baseColor, -30), // Version plus foncée
      '#FFD700', // Or
      '#FF6B6B'  // Rouge/rose pour contraste
    ];
    const confettiCount = 50;
    const container = document.querySelector('.onboarding-overlay');

    for (let i = 0; i < confettiCount; i++) {
      const confetti = document.createElement('div');
      confetti.className = 'confetti';
      confetti.style.left = Math.random() * 100 + '%';
      confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      confetti.style.animationDelay = Math.random() * 0.5 + 's';
      confetti.style.animationDuration = Math.random() * 2 + 2 + 's';
      container.appendChild(confetti);

      setTimeout(() => {
        confetti.remove();
      }, 4000);
    }
  };

  const currentStepData = steps[currentStep];

  return (
    <div className={`onboarding-overlay ${isExiting ? 'exiting' : ''}`}>
      <div className="onboarding-backdrop" onClick={handleSkipOnboarding}></div>
      
      <div className={`onboarding-modal ${currentStepData.id === 'complete' ? 'complete' : ''}`}>
        {/* Header */}
        <div className="onboarding-header">
          <div className="onboarding-progress-bar">
            <div 
              className="onboarding-progress-fill" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="onboarding-step-info">
            <span className="step-counter">Étape {currentStep + 1}/{totalSteps}</span>
            <button 
              className="onboarding-skip-btn" 
              onClick={handleSkipOnboarding}
              title="Passer la visite"
            >
              ✕ Passer
            </button>
          </div>
        </div>

        {/* Contenu */}
        <div className="onboarding-body">
          <div className="onboarding-title-section">
            <h1 className="onboarding-title">{currentStepData.title}</h1>
            <p className="onboarding-subtitle">{currentStepData.description}</p>
          </div>
          
          <div className="onboarding-content-section">
            {currentStepData.content}
          </div>
        </div>

        {/* Footer avec boutons */}
        <div className="onboarding-footer">
          {currentStep === totalSteps - 1 ? (
            // Dernière étape : boutons d'action spécifiques
            <>
              <button 
                className="onboarding-btn btn-secondary" 
                onClick={handlePrevious}
              >
                ← Précédent
              </button>
              
              <div className="onboarding-final-actions">
                <button 
                  className="onboarding-btn btn-outline" 
                  onClick={handleGoToSettings}
                  title="Aller aux réglages pour configurer votre entreprise"
                >
                  ⚙️ Aller aux réglages
                </button>
                <button 
                  className="onboarding-btn btn-primary" 
                  onClick={handleCompleteWithConfetti}
                >
                  🎉 Terminer
                </button>
              </div>
            </>
          ) : (
            // Étapes normales : navigation standard
            <>
              <button 
                className="onboarding-btn btn-secondary" 
                onClick={handlePrevious}
                disabled={currentStep === 0}
              >
                ← Précédent
              </button>
              
              <div className="onboarding-dots">
                {steps.map((_, index) => (
                  <span 
                    key={index} 
                    className={`dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                    onClick={() => setCurrentStep(index)}
                  ></span>
                ))}
              </div>

              <button 
                className="onboarding-btn btn-primary" 
                onClick={handleNext}
              >
                Suivant →
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
